import React, { useState, useContext, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { AuthContext } from '../context/AuthContext';
import Layout from '../components/Layout';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const recaptchaSiteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').trim();
  const recaptchaRequired = !!recaptchaSiteKey;

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (recaptchaRequired && !recaptchaToken) {
      setError('Proszę potwierdzić, że nie jesteś botem (reCAPTCHA)');
      return;
    }
    setLoading(true);
    try {
      await login(email, password, recaptchaToken || null);
      navigate('/dashboard');
    } catch (err) {
      const d = err.response?.data;
      setError(
        (d && (d.detail || d.recaptcha_token?.[0] || d.non_field_errors?.[0])) ||
          'Błąd logowania. Sprawdź dane i spróbuj ponownie.'
      );
      // Reset reCAPTCHA przy błędzie
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-ik-black">
              Zaloguj się do konta
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Lub{' '}
              <Link to="/register" className="font-medium text-ik-orange hover:text-orange-600">
                zarejestruj nowe konto
              </Link>
            </p>
            <p className="mt-1 text-center text-sm text-gray-600">
              Kominiarz z wpisem z importu?{' '}
              <Link to="/request-claim-chimney" className="font-medium text-ik-orange hover:text-orange-600">
                Przejmij wpis
              </Link>
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email lub nazwa użytkownika
                </label>
                <input
                  id="email"
                  name="email"
                  type="text"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange focus:z-10 sm:text-sm"
                  placeholder="Email lub nazwa użytkownika"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Hasło
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange focus:z-10 sm:text-sm"
                  placeholder="Hasło"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {recaptchaSiteKey && (
              <div className="flex justify-center mb-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={recaptchaSiteKey}
                  onChange={handleRecaptchaChange}
                />
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || (recaptchaRequired && !recaptchaToken)}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-ik-orange hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ik-orange disabled:opacity-50"
              >
                {loading ? 'Logowanie...' : 'Zaloguj się'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Login;
